"use client";

import { useState, useTransition } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { createWalletAction } from "@/app/actions";
import { CHAINS } from "@/lib/format";

/**
 * Provisioning strip on the customer's Account page.
 * Virtual accounts go through the requirements-driven form (/account/provision).
 * Wallet creation assumes the non-custodial claim is done (WalletClaim handles that).
 */
export function ProvisionControls({
  customerId,
  hasVirtualAccount,
  walletReady,
}: {
  customerId: string;
  hasVirtualAccount: boolean;
  walletReady: boolean;
}) {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [chain, setChain] = useState<string>("base");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Nothing to offer (VA handled, wallet creation not unlocked) — render nothing.
  // Wallet setup itself is the WalletClaim card, rendered separately.
  if (hasVirtualAccount && !walletReady) return null;

  function createWallet() {
    setMsg(null);
    setErr(null);
    start(async () => {
      const res = await createWalletAction(customerId, chain);
      if (res.ok) {
        setMsg(`Wallet on ${chain} requested — the address arrives when it activates.`);
        router.refresh();
      } else {
        setErr(res.error ?? "Request failed");
      }
    });
  }

  return (
    <div className="card-cream p-6">
      <div className="eyebrow mb-4" style={{ color: "var(--c-ink-soft)" }}>
        provision your account
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {!hasVirtualAccount && (
          <Link href="/account/provision?type=virtual_account&asset=USD" className="btn btn-dark">
            Request USD virtual account
          </Link>
        )}
        {walletReady && (
          <div className="flex items-center gap-2">
            <select className="field w-[140px]" value={chain} onChange={(e) => setChain(e.target.value)}>
              {CHAINS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <button className="btn btn-primary" disabled={pending} onClick={createWallet}>
              Create wallet
            </button>
          </div>
        )}
      </div>
      {msg && <p className="mt-3 text-[14px] font-light">{msg}</p>}
      {err && (
        <p className="mt-3 text-[14px] font-light" style={{ color: "var(--c-danger)" }}>
          {err}
        </p>
      )}
    </div>
  );
}
